import type { Unsubscribe } from 'firebase/firestore'
import { doc, onSnapshot } from 'firebase/firestore'
import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { selectIsLoggedIn, selectToken } from '@store/actionSlices/token'

import {
  FULFILLED_STATUS,
  REJECTED_STATUS,
} from '@components/data-handler/data-handler'

import { useLazyHealthCheckQuery } from '@api/health-check'

import { getQueryStringParams } from '@adUtilities/helper'
import { hasToken } from '@adUtilities/token-helper'

import firestore from './firebase-db'

const initFirebaseSnapshot = (baseQuery: string, ...queryInit: string[]) => {
  const location = useLocation()
  const unsub = React.useRef<Unsubscribe | null>(null)

  const token = useSelector(selectToken)
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const [enablePolling, setEnablePolling] = React.useState(false)
  const [firebaseIsActive, setFirebaseActive] = React.useState(false)
  const [pollingInterval, setPollingInterval] = React.useState(0)
  const [reloadWhenInternetisBackUp, setReloadWhenInternetisBackUp] =
    React.useState(false)
  const [firebaseData, setFirebaseData] = React.useState<any>(undefined)

  const [fetch, { isLoading, isError, status }] = useLazyHealthCheckQuery({
    pollingInterval: isLoggedIn ? pollingInterval : 0,
  })

  React.useEffect(() => {
    if (
      !isLoading &&
      isError &&
      !reloadWhenInternetisBackUp &&
      status === REJECTED_STATUS
    ) {
      setReloadWhenInternetisBackUp(true)
      console.error(
        'Showcase Alert: Unable to reach the server. Please check your internet connection.'
      )
      return
    }
    if (
      !isLoading &&
      !isError &&
      reloadWhenInternetisBackUp &&
      status === FULFILLED_STATUS
    ) {
      window.location.reload()
    }
  }, [isLoading, isError, reloadWhenInternetisBackUp, status])

  React.useEffect(() => {
    if (firebaseIsActive && enablePolling) {
      setPollingInterval(30000)
      fetch()
      return
    }
    setPollingInterval(0)
  }, [firebaseIsActive, enablePolling])

  const createSnapShot = () => {
    if (firestore) {
      unsub.current = onSnapshot(
        doc(firestore, baseQuery, ...queryInit),
        (doc) => {
          if (!doc.exists()) {
            setFirebaseData(undefined)
            setFirebaseActive(false)
          }
          setFirebaseActive(true)
          setFirebaseData(doc.data())
        }
      )
    }
  }

  const validateQuery = (query: string[]) =>
    query.filter((res) => res).length >= 3

  React.useEffect(() => {
    if (!hasToken(token)) {
      unsub.current?.()
      return undefined
    }

    if (baseQuery && validateQuery(queryInit)) {
      createSnapShot()
    }
    return () => {
      unsub.current?.()
    }
  }, [token, [baseQuery, ...queryInit].toString()])

  React.useEffect(() => {
    const queryEnablePolling = getQueryStringParams(location.search)?.polling
    if (
      isLoggedIn &&
      (queryEnablePolling === 'true' || queryEnablePolling === 'false')
    ) {
      localStorage.setItem('polling', queryEnablePolling)
      setEnablePolling(queryEnablePolling === 'true')
      return
    }
    const localStorageEnablePolling = localStorage.getItem('polling') === 'true'
    setEnablePolling(localStorageEnablePolling)
  }, [isLoggedIn])

  return firebaseData
}

export default initFirebaseSnapshot
